/**
 * Mach — Build Your Kit (bundle builder)
 *
 * A <bundle-builder> web component that lets a customer assemble a 2-piece kit
 * (top + bottom) and add both line items in a single /cart/add.js request, then
 * refreshes and opens the theme's cart drawer.
 *
 * Data is read from a JSON <script class="bundle-builder__data"> child (emitted by
 * sections/bundle-builder.liquid). No framework — vanilla + event delegation, matching
 * the theme's conventions.
 */
(function () {
  if (customElements.get('bundle-builder')) return;

  const SLOTS = ['top', 'bottom'];

  class BundleBuilder extends HTMLElement {
    connectedCallback() {
      try {
        this.data = JSON.parse(this.querySelector('.bundle-builder__data').textContent);
      } catch (e) {
        console.error('[bundle-builder] bad data', e); // eslint-disable-line
        return;
      }
      this.settings = this.data.settings || {};
      this.discountPercent = Number(this.dataset.discountPercent || this.settings.discountPercent || 0);
      this.disciplines = this.data.disciplines || [];
      if (!this.disciplines.length) return;

      this.previewEl = this.querySelector('[data-bb-preview]');
      this.slotsEl = this.querySelector('[data-bb-slots]');
      this.summaryEl = this.querySelector('[data-bb-summary]');
      this.modalEl = this.querySelector('[data-bb-modal]');
      this.hasSizeGuide = !!this.modalEl;
      this.genderEl = this.querySelector('[data-bb-gender]');

      // selection is keyed by discipline + gender (see selKey)
      const firstDisc = this.disciplines[0];
      this.state = { disciplineId: firstDisc.id, gender: this.availableGenders(firstDisc)[0] || 'men', selection: {} };

      this.bindTabs();
      this.bindGenderToggle();
      this.wireModal();
      this.bindSizeRec();
      this.render();
    }

    // ---- size-guide modal (reuses the sizing-chart page content) -------------
    wireModal() {
      if (!this.modalEl) return;
      this.modalEl.querySelectorAll('[data-bb-modal-close]').forEach((el) =>
        el.addEventListener('click', () => this.closeSizeGuide())
      );
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !this.modalEl.hidden) this.closeSizeGuide(); });
    }
    openSizeGuide(slot) {
      // If a size-guide page is configured, show it in the modal (right in the builder).
      if (this.modalEl) { this.modalEl.hidden = false; document.body.style.overflow = 'hidden'; return; }
      // Otherwise fall back to the garment's PDP, where the EyeFitU widget runs.
      const s = this.sel(slot);
      const p = s.productId ? this.productById(slot, s.productId) : null;
      if (p && p.url) window.open(p.url, '_blank');
    }
    closeSizeGuide() { if (this.modalEl) { this.modalEl.hidden = true; document.body.style.overflow = ''; } }

    // ---- EyeFitU size recommendation (per slot) ------------------------------
    // The EyeFitU SDK is loaded store-wide by the app. It exposes a GA-style
    // command API: window.eyefitu('init', undefined, options). We mount a
    // "Find my size" widget per slot for the currently selected garment; the
    // backend resolves the correct size chart from the productId. The result
    // comes back as a bubbling `eyefitu` DOM event we map onto the size buttons.
    bindSizeRec() {
      this._recBySlot = {};      // slot -> { productId, size } last recommendation
      this._efSig = {};          // slot -> productId|gender currently mounted
      this._efHost = {};         // slot -> persistent host element (survives re-render)
      // recommendedSize/selectedSize bubble up from each widget's own element
      this.addEventListener('eyefitu', (e) => this.onSizeRec(e));
    }

    // Queue-safe call: use the SDK if ready, else push to its pending queue
    // (drained when sdk.js finishes loading), mirroring the app's own wrapper.
    efCall() {
      const args = Array.prototype.slice.call(arguments);
      if (typeof window.eyefitu === 'function') { try { window.eyefitu.apply(null, args); } catch (e) { /* noop */ } }
      else { (window.eyefituCalls = window.eyefituCalls || []).push(args); }
    }

    mountSizeRec() {
      SLOTS.forEach((slot) => {
        const mount = this.slotsEl.querySelector('.bb-sizerec-mount[data-slot="' + slot + '"]');
        if (!mount) { this._efSig[slot] = null; return; } // no size row yet (no colour chosen)
        const r = this.resolveVariant(slot);
        if (!r || !r.product) { this._efSig[slot] = null; return; }
        const p = r.product;

        // Persistent host so the mounted widget isn't destroyed on a size click.
        let host = this._efHost[slot];
        if (!host) {
          host = document.createElement('span');
          host.className = 'bb-sizerec';
          host.id = 'bb-ef-' + slot;
          this._efHost[slot] = host;
        }
        mount.appendChild(host); // relocate into the freshly rendered slot

        // Only (re)initialise when the garment or gender actually changed.
        const sig = p.id + '|' + this.state.gender;
        if (this._efSig[slot] === sig && host.children.length) return;
        this._efSig[slot] = sig;
        host.innerHTML = '';

        const sizes = [];
        p.variants.forEach((v) => { if (v.size && !sizes.includes(v.size)) sizes.push(v.size); });

        this.efCall('init', undefined, {
          selector: '#bb-ef-' + slot,
          place: 'append',
          dataAttributes: {
            productId: 'gid://shopify/Product/' + p.id,
            brand: p.vendor || 'Mach Apparel',
            product: p.type || '',
            gender: this.state.gender === 'women' ? 'female' : 'male',
            language: 'en',
            sizes: JSON.stringify(sizes.length ? sizes : ['all']),
            visualSizeChart: true,
            visualSizeChartTitle: (p.vendor || 'Mach Apparel') + (p.type ? ' - ' + p.type : ''),
            flow: 'mac',
            injection: 'my-size',
            defaultUnits: 'imperial',
          },
        });
      });

      // If the SDK never mounts a button (app missing), fall back to the static
      // size-guide modal so the row still has a working "size guide" affordance.
      if (this.hasSizeGuide) {
        clearTimeout(this._efFallbackTimer);
        this._efFallbackTimer = setTimeout(() => this.sizeRecFallback(), 4000);
      }
    }

    sizeRecFallback() {
      SLOTS.forEach((slot) => {
        const host = this._efHost[slot];
        if (!host || host.children.length || !host.isConnected) return;
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'bb-sizeguide';
        b.textContent = 'Size guide';
        b.addEventListener('click', () => this.openSizeGuide(slot));
        host.appendChild(b);
      });
    }

    // EyeFitU size labels can differ from option values (XXL vs 2XL). Normalise.
    matchSizeLabel(product, label) {
      const norm = (s) => String(s).toUpperCase().replace(/\s+/g, '')
        .replace(/^XXXS$/, '3XS').replace(/^XXS$/, '2XS')
        .replace(/^XXXL$/, '3XL').replace(/^XXL$/, '2XL');
      const target = norm(label);
      const sizes = [];
      product.variants.forEach((v) => { if (v.size && !sizes.includes(v.size)) sizes.push(v.size); });
      return sizes.find((s) => norm(s) === target) || null;
    }

    onSizeRec(e) {
      const size = e.detail && (e.detail.recommendedSize || e.detail.selectedSize);
      if (!size) return;
      // resolve slot from the host element the widget lives in
      let slot = null;
      const host = e.target && e.target.closest && e.target.closest('.bb-sizerec');
      if (host && host.id.indexOf('bb-ef-') === 0) slot = host.id.slice('bb-ef-'.length);
      if (!slot || SLOTS.indexOf(slot) === -1) return;
      const r = this.resolveVariant(slot);
      if (!r || !r.product) return;
      const match = this.matchSizeLabel(r.product, size);
      if (!match) return;
      const avail = r.product.variants.some((v) => v.size === match && (r.product.colorIndex < 0 || v.color === this.sel(slot).color) && v.available);
      this._recBySlot[slot] = { productId: r.product.id, size: match };
      if (avail) this.setSel(slot, { size: match });
      this.render();
    }

    // ---- helpers -------------------------------------------------------------

    get discipline() {
      return this.disciplines.find((d) => d.id === this.state.disciplineId) || this.disciplines[0];
    }

    // Genders configured for a discipline (keys of its `genders` map, order preserved: men, women).
    availableGenders(discipline) {
      const d = discipline || this.discipline;
      return Object.keys((d && d.genders) || {});
    }

    // Slots for the active gender (fall back to the first available gender).
    currentSlots() {
      const g = (this.discipline && this.discipline.genders) || {};
      const active = g[this.state.gender] || g[this.availableGenders()[0]];
      return (active && active.slots) || [];
    }

    slotDef(slot) {
      return this.currentSlots().find((s) => s.slot === slot);
    }

    // Selection is scoped per discipline + gender, so switching either keeps its own choices.
    selKey() { return this.state.disciplineId + '|' + this.state.gender; }
    sel(slot) {
      return (this.state.selection[this.selKey()] || {})[slot] || {};
    }
    setSel(slot, patch) {
      const perKey = (this.state.selection[this.selKey()] = this.state.selection[this.selKey()] || {});
      perKey[slot] = Object.assign({}, perKey[slot], patch);
    }

    productById(slot, id) {
      return this.slotDef(slot).products.find((p) => String(p.id) === String(id));
    }

    // Unique, order-preserving option values across a product's variants
    valuesFor(product, key) {
      const seen = [];
      product.variants.forEach((v) => {
        const val = v[key];
        if (val && !seen.includes(val)) seen.push(val);
      });
      return seen;
    }

    variantFor(product, color, size) {
      return product.variants.find(
        (v) => (color == null || v.color === color) && (size == null || v.size === size)
      );
    }

    money(cents) {
      const n = (Number(cents) || 0) / 100;
      const s = n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return '$' + s; // store currency is USD; swap for a money_format parse if multi-currency
    }

    // ---- state resolution ----------------------------------------------------

    resolveVariant(slot) {
      const s = this.sel(slot);
      if (!s.productId) return null;
      const product = this.productById(slot, s.productId);
      if (!product) return null;
      const hasColor = product.colorIndex >= 0;
      const hasSize = product.sizeIndex >= 0;
      const variant = this.variantFor(product, hasColor ? s.color : null, hasSize ? s.size : null);
      return { product, variant: variant || null };
    }

    bothChosen() {
      return SLOTS.every((slot) => {
        const r = this.resolveVariant(slot);
        return r && r.variant && r.variant.available;
      });
    }

    // ---- rendering -----------------------------------------------------------

    bindTabs() {
      this.querySelectorAll('.bundle-builder__discipline-tab').forEach((tab) => {
        tab.addEventListener('click', () => {
          this.state.disciplineId = tab.dataset.discipline;
          this.querySelectorAll('.bundle-builder__discipline-tab').forEach((t) =>
            t.setAttribute('aria-selected', t === tab ? 'true' : 'false')
          );
          this.render();
        });
      });
    }

    // Auto-select the sole product in a slot so the customer starts mid-flow
    ensureDefaults() {
      SLOTS.forEach((slot) => {
        const def = this.slotDef(slot);
        if (def && def.products.length === 1 && !this.sel(slot).productId) {
          const only = def.products[0];
          const firstAvail = only.variants.find((v) => v.available) || only.variants[0];
          this.setSel(slot, {
            productId: only.id,
            color: only.colorIndex >= 0 ? firstAvail && firstAvail.color : null,
            size: only.sizeIndex >= 0 ? firstAvail && firstAvail.size : null,
          });
        }
      });
    }

    renderGenderToggle() {
      if (!this.genderEl) return;
      const genders = this.availableGenders();
      if (genders.length < 2) { this.genderEl.innerHTML = ''; return; }
      const labels = { men: this.genderEl.dataset.menLabel || 'Men', women: this.genderEl.dataset.womenLabel || 'Women' };
      this.genderEl.innerHTML = genders.map((g) =>
        '<button type="button" class="bb-gender__btn' + (g === this.state.gender ? ' is-active' : '') +
        '" data-bb-gender-btn data-gender="' + g + '" aria-pressed="' + (g === this.state.gender) + '">' +
        this.escape(labels[g] || g) + '</button>'
      ).join('');
    }

    bindGenderToggle() {
      if (!this.genderEl) return;
      this.genderEl.addEventListener('click', (e) => {
        const btn = e.target.closest && e.target.closest('[data-bb-gender-btn]');
        if (!btn) return;
        const g = btn.dataset.gender;
        if (!g || g === this.state.gender) return;
        this.state.gender = g;
        this.render();
      });
    }

    render() {
      // Keep the active gender valid for the current discipline (some may be men-only).
      if (this.availableGenders().indexOf(this.state.gender) === -1) {
        this.state.gender = this.availableGenders()[0];
      }
      this.renderGenderToggle();
      this.renderSlots();
      this.renderPreview();
      this.renderSummary();
    }

    renderSlots() {
      this.slotsEl.innerHTML = SLOTS.map((slot) => this.renderSlot(slot)).join('');
      this.wireSlotEvents();
      this.mountSizeRec();
    }

    // Group a slot's products by their kit_model metafield. Products sharing a value are
    // the same garment (color collections); different values are separate models.
    slotModels(slot) {
      const groups = [];
      const byModel = {};
      this.slotDef(slot).products.forEach((p) => {
        const key = (p.kitModel || '').trim();
        if (!byModel[key]) { byModel[key] = { model: p.kitModel || '', products: [] }; groups.push(byModel[key]); }
        byModel[key].products.push(p);
      });
      return groups;
    }
    currentModel(slot) {
      const models = this.slotModels(slot);
      return models.find((m) => m.model === this.sel(slot).model) || models[0] || { model: '', products: [] };
    }
    // Flatten a product list into one colorway list; each entry maps back to product + variants.
    colorwaysFor(products) {
      const out = [];
      const build = (productId, color, swatch, vs) => {
        const first = vs[0] || {};
        const matchIds = [];
        vs.forEach((v) => (v.kitMatch || []).forEach((id) => { const s = String(id); if (matchIds.indexOf(s) === -1) matchIds.push(s); }));
        return {
          productId: productId, color: color, swatch: swatch || {},
          tier: first.designTier || '', chip: first.swatchChip || '',
          variants: vs, variantIds: vs.map((v) => String(v.id)), matchIds: matchIds
        };
      };
      products.forEach((p) => {
        if (p.colorIndex < 0) { out.push(build(p.id, null, {}, p.variants)); return; }
        this.valuesFor(p, 'color').forEach((c) => {
          out.push(build(p.id, c, (p.swatches && p.swatches[c]) || {}, p.variants.filter((v) => v.color === c)));
        });
      });
      return out;
    }

    // The colourway currently selected in a slot (used to read its curated kit_match).
    selectedColorway(slot) {
      const s = this.sel(slot);
      if (s.productId == null) return null;
      return this.colorwaysFor(this.currentModel(slot).products)
        .find((c) => String(c.productId) === String(s.productId) && c.color === s.color) || null;
    }

    // Is this bottom colourway a match for the selected top?
    // Prefer the curated kit_match variant refs; fall back to same colour name.
    isMatch(cw, ctx) {
      if (!ctx) return false;
      if (ctx.matchIds && ctx.matchIds.size) return cw.variantIds.some((id) => ctx.matchIds.has(id));
      if (ctx.topColor && cw.color) return cw.color.toLowerCase() === ctx.topColor.toLowerCase();
      return false;
    }

    // Order colourways into design-tier groups (mirrors the PDP: Core / Limited Edition).
    // Returns [{ tier, label, items }]; a single untitled group when no tiers are set.
    tierGroups(colorways) {
      if (!colorways.some((cw) => cw.tier)) return [{ tier: '', label: '', items: colorways }];
      const labels = this.settings.tierLabels || {};
      const labelFor = (t) => labels[String(t).toLowerCase()] || (t ? t + ' Colors' : '');
      const order = (this.settings.tierOrder || []).slice();
      // append any tiers not in the configured order, then the untitled group last
      colorways.forEach((cw) => { if (cw.tier && order.indexOf(cw.tier) === -1) order.push(cw.tier); });
      const groups = [];
      order.forEach((t) => {
        const items = colorways.filter((cw) => cw.tier === t);
        if (items.length) groups.push({ tier: t, label: labelFor(t), items: items });
      });
      const untitled = colorways.filter((cw) => !cw.tier);
      if (untitled.length) groups.push({ tier: '', label: '', items: untitled });
      return groups;
    }

    swatchHtml(cw, s, slot, matchCtx) {
      const active = String(cw.productId) === String(s.productId) && cw.color === s.color;
      const match = this.isMatch(cw, matchCtx);
      const anyAvail = cw.variants.some((v) => v.available);
      const dotImg = cw.chip || cw.swatch.image;
      const dotStyle = dotImg
        ? ' style="background-image:url(' + dotImg + ')"'
        : (cw.swatch.color ? ' style="background:rgb(' + cw.swatch.color + ')"' : '');
      // Chip-only (name shown once in the field label, like the PDP); colour name kept as title/aria.
      const label = this.escape(cw.color || '') + (match ? ' — matches your top' : '');
      return (
        '<button type="button" class="bb-swatch' +
        (active ? ' is-active' : '') + (match ? ' is-match' : '') + (anyAvail ? '' : ' is-oos') +
        '" data-bb-color data-slot="' + slot + '" data-design="' + cw.productId + '" data-value="' + this.escape(cw.color || '') + '"' +
        ' title="' + label + '" aria-label="' + label + '">' +
        '<span class="bb-swatch__dot"' + dotStyle + '></span>' +
        (match ? '<span class="bb-swatch__match">Match</span>' : '') +
        '</button>'
      );
    }

    renderSlot(slot) {
      const def = this.slotDef(slot);
      const s = this.sel(slot);
      const step = slot === 'top' ? 1 : 2;
      const models = this.slotModels(slot);
      const model = this.currentModel(slot);
      const colorways = this.colorwaysFor(model.products);
      // Match context for the bottom slot: the selected top's curated kit_match refs (+ colour fallback).
      let matchCtx = null;
      if (slot === 'bottom') {
        const topCw = this.selectedColorway('top');
        const ids = new Set();
        if (topCw) (topCw.matchIds || []).forEach((id) => ids.add(String(id)));
        matchCtx = { matchIds: ids, topColor: this.sel('top').color || null };
      }

      // Model selector — only shown when a slot has more than one model (e.g. Aero / Climber).
      let modelRow = '';
      if (models.length > 1) {
        modelRow =
          '<div class="bb-field"><span class="bb-field__label">Model' +
          (model.model ? ': <b>' + this.escape(model.model) + '</b>' : '') + '</span>' +
          '<div class="bb-models">' +
          models.map((m) => {
            const active = m === model;
            return '<button type="button" class="bb-chip' + (active ? ' is-active' : '') +
              '" data-bb-model data-slot="' + slot + '" data-model="' + this.escape(m.model) + '">' +
              this.escape(m.model || 'Standard') + '</button>';
          }).join('') +
          '</div></div>';
      }

      // Colorway swatches — grouped by design tier (Core / Limited Edition), mirroring the PDP.
      let colorRow = '';
      if (colorways.length) {
        const groupsHtml = this.tierGroups(colorways).map((g) => {
          const heading = g.label ? '<span class="bb-swatch-group__label">' + this.escape(g.label) + '</span>' : '';
          return '<div class="bb-swatch-group">' + heading +
            '<div class="bb-swatches">' + g.items.map((cw) => this.swatchHtml(cw, s, slot, matchCtx)).join('') + '</div></div>';
        }).join('');
        colorRow =
          '<div class="bb-field"><span class="bb-field__label">Color' +
          (s.color ? ': <b>' + this.escape(s.color) + '</b>' : '') + '</span>' +
          groupsHtml +
          '</div>';
      }

      // Sizes for the selected colorway
      let sizeRow = '';
      const cw = s.productId != null ? colorways.find((c) => String(c.productId) === String(s.productId) && c.color === s.color) : null;
      if (cw) {
        const sizes = [];
        cw.variants.forEach((v) => { if (v.size && !sizes.includes(v.size)) sizes.push(v.size); });
        // EyeFitU "Find my size" mounts into this host (see mountSizeRec). Persists
        // across re-renders so it isn't torn down on every size click.
        const sizeRec = '<span class="bb-sizerec-mount" data-bb-ef-mount data-slot="' + slot + '"></span>';
        const rec = this._recBySlot && this._recBySlot[slot];
        const recNote =
          rec && String(rec.productId) === String(cw.productId) && rec.size
            ? '<p class="bb-sizerec-note">EyeFitU suggests <b>' + this.escape(rec.size) + '</b> — adjust anytime.</p>'
            : '';
        sizeRow =
          '<div class="bb-field"><div class="bb-field__labelrow"><span class="bb-field__label">Size' +
          (s.size ? ': <b>' + this.escape(s.size) + '</b>' : '') + '</span>' + sizeRec + '</div>' +
          '<div class="bb-sizes">' +
          sizes.map((sz) => {
            const active = sz === s.size;
            const avail = cw.variants.some((v) => v.size === sz && v.available);
            return (
              '<button type="button" class="bb-size' + (active ? ' is-active' : '') + (avail ? '' : ' is-oos') +
              '" data-bb-size data-slot="' + slot + '" data-value="' + this.escape(sz) + '"' + (avail ? '' : ' aria-disabled="true"') + '>' +
              this.escape(sz) + '</button>'
            );
          }).join('') +
          '</div>' + recNote + '</div>';
      }

      const anyMatch = matchCtx && colorways.some((cw) => this.isMatch(cw, matchCtx));
      const hint =
        slot === 'bottom' && this.sel('top').productId != null && s.productId == null && anyMatch
          ? '<p class="bb-slot__hint">Complete your kit — the match is marked below</p>'
          : '';

      return (
        '<section class="bb-slot" data-slot="' + slot + '">' +
        '<header class="bb-slot__head"><span class="bb-slot__step">' + step + '</span>' +
        '<h2 class="bb-slot__title">' + this.escape(def.label) + '</h2></header>' +
        hint + modelRow + colorRow + sizeRow +
        '</section>'
      );
    }

    renderPreview() {
      const imgs = SLOTS.map((slot) => {
        const r = this.resolveVariant(slot);
        const s = this.sel(slot);
        let src = '';
        if (r) {
          const product = r.product;
          // pick image for the chosen color, else variant image, else featured
          if (s.color) {
            const cv = product.variants.find((v) => v.color === s.color && v.image);
            src = (cv && cv.image) || (r.variant && r.variant.image) || product.featured;
          } else {
            src = (r.variant && r.variant.image) || product.featured;
          }
        }
        const label = this.slotDef(slot).label;
        return (
          '<div class="bb-preview__pane bb-preview__pane--' + slot + '">' +
          (src
            ? '<img src="' + src + '" alt="' + this.escape(label) + '" loading="lazy">'
            : '<div class="bb-preview__placeholder">' + this.escape(label) + '</div>') +
          '</div>'
        );
      }).join('');
      this.previewEl.innerHTML = '<div class="bb-preview__stack">' + imgs + '</div>';
    }

    renderSummary() {
      let subtotal = 0;
      const rows = SLOTS.map((slot) => {
        const r = this.resolveVariant(slot);
        const def = this.slotDef(slot);
        if (r && r.variant) {
          subtotal += r.variant.price;
          return (
            '<div class="bb-sum__row"><span>' + this.escape(r.product.title) +
            (r.variant.title && r.variant.title !== 'Default Title'
              ? ' <em>' + this.escape(r.variant.title) + '</em>'
              : '') +
            '</span><span>' + this.money(r.variant.price) + '</span></div>'
          );
        }
        return '<div class="bb-sum__row bb-sum__row--empty"><span>' + this.escape(def.label) + '</span><span>—</span></div>';
      }).join('');

      const ready = this.bothChosen();
      const saveCents = ready && this.discountPercent > 0 ? Math.round(subtotal * (this.discountPercent / 100)) : 0;
      const total = subtotal - saveCents;

      const savingsHtml =
        this.settings.showSavings && this.discountPercent > 0
          ? '<div class="bb-sum__save' + (saveCents ? ' is-on' : '') + '">' +
            (saveCents ? 'You save ' + this.money(saveCents) + ' (' + this.discountPercent + '%)' : 'Save ' + this.discountPercent + '% when you complete the kit') +
            '</div>'
          : '';

      this.summaryEl.innerHTML =
        '<div class="bb-sum">' +
        rows +
        savingsHtml +
        '<div class="bb-sum__total"><span>Kit total</span><span>' + (subtotal ? this.money(total) : '—') + '</span></div>' +
        '<button type="button" class="bb-add btn btn--primary" data-bb-add' + (ready ? '' : ' aria-disabled="true"') + '>' +
        (ready ? 'Add kit to cart' : 'Choose a top and bottom') +
        '</button>' +
        '<p class="bb-sum__error" data-bb-error hidden></p>' +
        '</div>';

      const addBtn = this.summaryEl.querySelector('[data-bb-add]');
      if (addBtn && ready) addBtn.addEventListener('click', () => this.addKit(addBtn));
    }

    // ---- events --------------------------------------------------------------

    wireSlotEvents() {
      this.slotsEl.querySelectorAll('[data-bb-model]').forEach((el) =>
        el.addEventListener('click', () => {
          // switching model clears the colorway/size choice for that slot
          this.setSel(el.dataset.slot, { model: el.dataset.model, productId: undefined, color: undefined, size: undefined });
          this.render();
        })
      );

      this.slotsEl.querySelectorAll('[data-bb-color]').forEach((el) =>
        el.addEventListener('click', () => {
          const slot = el.dataset.slot;
          const productId = el.dataset.design;
          const color = el.dataset.value || null;
          const product = this.productById(slot, productId);
          const inColor = product.variants.filter((v) => v.color === color || (color === null));
          // keep the current size if this colorway offers it, else first available
          let size = this.sel(slot).size;
          if (!inColor.some((v) => v.size === size && v.available)) {
            const alt = inColor.find((v) => v.available) || inColor[0];
            size = alt ? alt.size : size;
          }
          this.setSel(slot, { productId, color, size });
          this.render();
        })
      );

      this.slotsEl.querySelectorAll('[data-bb-size]').forEach((el) =>
        el.addEventListener('click', () => {
          if (el.getAttribute('aria-disabled') === 'true') return;
          this.setSel(el.dataset.slot, { size: el.dataset.value });
          this.render();
        })
      );
    }

    // ---- add to cart ---------------------------------------------------------

    async addKit(btn) {
      const items = [];
      const kitId = 'kit-' + Date.now();
      for (const slot of SLOTS) {
        const r = this.resolveVariant(slot);
        if (!r || !r.variant) return;
        items.push({
          id: r.variant.id,
          quantity: 1,
          properties: { _kit: kitId, _kit_type: this.discipline.label },
        });
      }

      btn.classList.add('is-loading');
      btn.setAttribute('aria-disabled', 'true');

      const drawer = document.querySelector('cart-drawer');
      const sectionIds = drawer && drawer.dataset.sectionId ? [drawer.dataset.sectionId] : [];

      try {
        const res = await fetch((window.theme && theme.routes && theme.routes.cartAdd) || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
          body: JSON.stringify({ items, sections: sectionIds, sections_url: window.location.pathname }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error((data && (data.description || data.message)) || 'Add failed');

        // refresh + open the cart drawer, mirroring product-form.js
        if (drawer && data.sections && data.sections[drawer.dataset.sectionId] && drawer.updateFromCartChange) {
          drawer.updateFromCartChange(data.sections[drawer.dataset.sectionId]);
        }
        this.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
        const jsDrawer = document.querySelector('.js-cart-drawer');
        if (jsDrawer && typeof jsDrawer.open === 'function') {
          jsDrawer.open(btn);
        } else {
          window.location.href = (window.theme && theme.routes && theme.routes.cart) || '/cart';
        }

        btn.classList.remove('is-loading');
        btn.classList.add('is-success');
        setTimeout(() => btn.classList.remove('is-success'), 2000);
        btn.removeAttribute('aria-disabled');
      } catch (err) {
        console.error('[bundle-builder]', err); // eslint-disable-line
        btn.classList.remove('is-loading');
        btn.removeAttribute('aria-disabled');
        const errEl = this.summaryEl.querySelector('[data-bb-error]');
        if (errEl) {
          errEl.textContent = 'Sorry — could not add the kit. Please try again.';
          errEl.hidden = false;
        }
      }
    }

    escape(str) {
      return String(str == null ? '' : str).replace(/[&<>"']/g, (c) =>
        ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])
      );
    }
  }

  customElements.define('bundle-builder', BundleBuilder);
})();
